import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, Facebook, MessageCircle, MessageSquare, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useShopSettings, isSectionOff } from "../lib/shopSettings";
import { CallRequestDialog } from "./CallRequestDialog";

export default function Footer() {
  const { t, ts, site, lang } = useLang();
  const settings = useShopSettings();
  const off = (key) => isSectionOff(settings, key);
  return (
    <footer className="border-t border-gold/10 bg-black/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <div className="mb-4 max-w-xs">
            <img src={site("img.nav_logo") || (lang === "en" ? "/logo-gold-t-en.png" : "/logo-gold-t.png")} alt="Mr Trophée" data-edit-image="img.nav_logo" className="w-full h-auto" data-testid="footer-logo" />
          </div>
          <p className="text-ivory/60 text-sm leading-relaxed max-w-xs">{t("footer.tagline")}</p>
        </div>
        <div>
          <p className="font-serif text-lg text-champagne mb-4">{t("footer.nav_title")}</p>
          <ul className="space-y-2 text-sm text-ivory/60">
            {!off("boutique") && <li><Link to="/boutique" className="hover:text-gold transition-colors duration-300">{t("footer.catalogue")}</Link></li>}
            {/* Page SEO de saison (demande du propriétaire, 2026-10-27) */}
            {!off("boutique") && <li><Link to="/trophees-hockey" data-testid="footer-hockey" className="hover:text-gold transition-colors duration-300">{lang === "en" ? "Hockey trophies" : "Trophées de hockey"}</Link></li>}
            {!off("boutique") && <li><Link to="/occasions" data-testid="footer-occasions" className="hover:text-gold transition-colors duration-300">{t("footer.occasions")}</Link></li>}
            {!off("boutique") && <li><Link to="/villes" data-testid="footer-villes" className="hover:text-gold transition-colors duration-300">{t("footer.villes")}</Link></li>}
            {!off("boutique") && <li><Link to="/livraison-main-propre" data-testid="footer-livraison" className="hover:text-gold transition-colors duration-300">{t("footer.livraison")}</Link></li>}
            {!off("parrainage") && <li><Link to="/parrainage" data-testid="footer-referral" className="hover:text-gold transition-colors duration-300">{t("footer.referral")}</Link></li>}
            {!off("badge") && <li><Link to="/badge-partenaire" data-testid="footer-badge" className="hover:text-gold transition-colors duration-300">{t("footer.badge")}</Link></li>}
            {!off("catalogues") && <li><Link to="/catalogues" className="hover:text-gold transition-colors duration-300">{t("footer.catalogues")}</Link></li>}
            {!off("realisations") && <li><Link to="/realisations" className="hover:text-gold transition-colors duration-300">{t("nav.realisations")}</Link></li>}
            {!off("a-propos") && <li><Link to="/a-propos" className="hover:text-gold transition-colors duration-300">{t("nav.about")}</Link></li>}
            {!off("faq") && <li><Link to="/faq" className="hover:text-gold transition-colors duration-300">{t("nav.faq")}</Link></li>}
            {!off("contact") && <li><Link to="/contact" className="hover:text-gold transition-colors duration-300">{t("nav.contact")}</Link></li>}
            <li><Link to="/rendez-vous" data-testid="footer-rendezvous" className="hover:text-gold transition-colors duration-300">{t("rdv.kicker")}</Link></li>
            <li><Link to="/confidentialite" data-testid="footer-privacy-link" className="hover:text-gold transition-colors duration-300">{t("privacy.nav_link")}</Link></li>
            <li><Link to="/conditions" data-testid="footer-terms-link" className="hover:text-gold transition-colors duration-300">{t("terms.nav_link")}</Link></li>
            <li><Link to="/retours" data-testid="footer-returns-link" className="hover:text-gold transition-colors duration-300">{t("returns.nav_link")}</Link></li>
            <li><Link to="/panier" className="hover:text-gold transition-colors duration-300">{t("footer.cart")}</Link></li>
            <li><Link to="/compte" className="hover:text-gold transition-colors duration-300">{t("footer.account")}</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-serif text-lg text-champagne mb-4">{t("footer.contact_title")}</p>
          <ul className="space-y-3 text-sm text-ivory/60">
            <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold shrink-0" /> {t("footer.location")}</li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gold shrink-0" />
              <a href={`tel:${ts("footer.phone").replace(/[^+\d]/g, "")}`} className="hover:text-gold transition-colors duration-300">{t("footer.phone")}</a>
              <a
                href={`sms:${ts("footer.phone").replace(/[^+\d]/g, "")}`}
                data-testid="footer-texto"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-gold/40 text-gold text-xs hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MessageSquare className="w-3.5 h-3.5" /> {t("footer.texto")}
              </a>
            </li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold shrink-0" /> info@trophee.ca</li>
            <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-gold shrink-0" /> {t("footer.hours")}</li>
            <li>
              <a
                href="https://fb.com/trophee.ca" target="_blank" rel="noreferrer"
                data-testid="footer-facebook"
                className="flex items-center gap-3 hover:text-gold transition-colors duration-300 group"
              >
                <Facebook className="w-4 h-4 text-gold shrink-0" /> fb.com/trophee.ca
              </a>
            </li>
            <li className="flex flex-wrap items-center gap-2 mt-1">
              <a
                href="https://m.me/trophee.ca" target="_blank" rel="noreferrer"
                data-testid="footer-messenger"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" /> {t("contact.messenger")}
              </a>
              <CallRequestDialog testid="footer-call"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-ink font-semibold hover:bg-champagne transition-colors duration-300" />
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-gold/10 pt-6 pb-28 md:pb-10 text-center text-xs text-ivory/65 tracking-wide">
        <p>© {new Date().getFullYear()} Mr Trophée — {t("footer.rights")}</p>
        {/* Liens légaux tout en bas du pied de page (demande client) — les mêmes
            pages que la colonne Navigation, mais visibles d'un coup d'œil */}
        <p className="mt-2.5 flex flex-col items-center justify-center gap-2.5 sm:flex-row sm:gap-4">
          <Link to="/confidentialite" data-testid="footer-bottom-privacy"
            className="hover:text-gold transition-colors duration-300 underline-offset-2 hover:underline">{t("privacy.nav_link")}</Link>
          <span className="hidden sm:inline text-ivory/25">·</span>
          <Link to="/conditions" data-testid="footer-bottom-terms"
            className="hover:text-gold transition-colors duration-300 underline-offset-2 hover:underline">{t("terms.nav_link")}</Link>
          <span className="hidden sm:inline text-ivory/25">·</span>
          <Link to="/retours" data-testid="footer-bottom-returns"
            className="hover:text-gold transition-colors duration-300 underline-offset-2 hover:underline">{t("returns.nav_link")}</Link>
        </p>
      </div>
    </footer>
  );
}
